import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';

import { LocationLiteModel, LocationModel } from 'models';

import { useDebounceEmitter } from 'core/hooks';
import { MultiSelectNgField, MultiSelectNgFieldProps } from 'core/ui/MultiSelectNg';

/**
 * Props for LocationMultiSelect component
 */
export type LocationMultiSelectProps = Omit<MultiSelectNgFieldProps<LocationModel | LocationLiteModel>, 'textField' | 'dataItemKey'> & {
  data: (LocationModel | LocationLiteModel)[];
  /** Minimum characters required before filtering starts (default: 2) */
  minSearchLength?: number;
  /** Maximum results to display (default: 50) */
  maxResults?: number;
  /** Debounce delay in milliseconds (default: 300) */
  debounceDelay?: number;
  /** Enable virtualization for large datasets */
  enableVirtualization?: boolean;
  /** Page size for virtualization (default: 80) */
  pageSize?: number;
  /** Item height for virtualization (default: 28) */
  itemHeight?: number;
};

/**
 * React Hook Form compatible LocationMultiSelect component with performance optimizations
 *
 * Pre-configured wrapper around MultiSelectNgField for LocationModel data.
 * Uses 'name' as textField and 'id' as dataItemKey, with custom tag rendering enabled.
 *
 * Performance Features:
 * - Minimum search length requirement (default: 2 characters)
 * - Debounced filtering to reduce processing
 * - Limited results display (default: 50 items)
 * - Optional virtualization for large datasets
 * - Prevents form submission on Enter key
 *
 * @example
 * <LocationMultiSelect
 *   data={locations}
 *   name="selectedLocations"
 *   required
 *   enableVirtualization={true}
 *   pageSize={80}
 * />
 */
export const LocationMultiSelect: FunctionComponent<LocationMultiSelectProps> = ({
  data,
  minSearchLength = 2,
  maxResults = 50,
  debounceDelay = 300,
  enableVirtualization = false,
  pageSize = 80,
  itemHeight = 28,
  onFilterChange,
  ...props
}) => {
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentFilterText, setCurrentFilterText] = useState('');
  const [debouncedFilterText, setDebouncedFilterText] = useState('');

  // Virtualization state
  const [currentPage, setCurrentPage] = useState(0);
  const [virtualTotal, setVirtualTotal] = useState(0);

  // Debounced filter emitter to reduce processing frequency
  const { emitDebounce: emitDebouncedFilter } = useDebounceEmitter((filterText: string) => {
    setDebouncedFilterText(filterText);
    setIsFiltering(false);
  }, debounceDelay);

  // Optimized data that respects search length and result limits
  const optimizedData = useMemo(() => {
    let filteredData = data;

    // Apply search filter if criteria met
    if (debouncedFilterText && debouncedFilterText.length >= minSearchLength) {
      const searchTerm = debouncedFilterText.toLowerCase();
      filteredData = data.filter(
        (location) => location.name?.toLowerCase().includes(searchTerm) || (location as LocationModel).code?.toString().toLowerCase().includes(searchTerm),
      );
    }

    // Update virtual total for pagination
    setVirtualTotal(filteredData.length);

    // If virtualization is enabled, return paginated data
    if (enableVirtualization) {
      const startIndex = currentPage * pageSize;
      return filteredData.slice(startIndex, startIndex + pageSize);
    }

    // Otherwise, limit results to prevent UI freeze
    return filteredData.slice(0, maxResults);
  }, [data, debouncedFilterText, minSearchLength, maxResults, enableVirtualization, currentPage, pageSize]);

  // Enhanced filter change handler with performance optimizations
  const handleFilterChange = useCallback(
    (event: MultiSelectFilterChangeEvent) => {
      const filterValue = event?.filter?.value || '';

      setIsFiltering(true);
      setCurrentFilterText(filterValue);
      setCurrentPage(0); // Reset to first page on filter change

      // Emit debounced filter update
      emitDebouncedFilter(filterValue);

      // Call original handler if provided
      onFilterChange?.(event);
    },
    [onFilterChange, emitDebouncedFilter],
  );

  // Handle virtualization page changes
  const handlePageChange = useCallback(
    (event: { skip?: number; take?: number }) => {
      const newPage = Math.floor((event.skip || 0) / pageSize);
      setCurrentPage(newPage);
    },
    [pageSize],
  );

  // Virtualization configuration
  const virtualConfig = enableVirtualization
    ? {
        total: virtualTotal,
        skip: currentPage * pageSize,
        pageSize: pageSize,
        onPageChange: handlePageChange,
      }
    : undefined;

  // Handle key down to prevent form submission on Enter
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      // Let Kendo handle the selection, but prevent form submission
    }
  }, []);

  // Performance feedback for debugging
  useEffect(() => {
    if (data.length > 1000 && !enableVirtualization) {
      console.warn(`LocationMultiSelect: Large dataset detected (${data.length} locations). Consider enabling virtualization for better performance.`);
    }
  }, [data.length, enableVirtualization]);

  return (
    <div onKeyDown={handleKeyDown}>
      <MultiSelectNgField
        {...props}
        data={optimizedData}
        textField="name"
        dataItemKey="id"
        enableCustomTagRender={true}
        requiredMessage="Location is required"
        onFilterChange={handleFilterChange}
        virtual={virtualConfig}
      />
      {isFiltering && <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>Filtering locations...</div>}
      {enableVirtualization && data.length > 1000 && (
        <div style={{ fontSize: '12px', color: '#f0ad4e', marginTop: '4px', fontStyle: 'italic' }}>Virtualization enabled for {data.length} locations</div>
      )}
    </div>
  );
};
